import { useState, useEffect, useCallback } from 'react'
import { contactsApi, echangesApi } from '../api'
import { useApp } from '../AppContext'
import { BadgeStatut, Avatar, FormGroup, Empty, Loading, fmtDate, initials, fmt, Pagination } from '../components/ui'

export default function Contacts() {
  const { toast } = useApp()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)   // null | 'create' | contact
  const [detail, setDetail] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    contactsApi.list({ page, per_page: 25, search })
      .then(r => { setItems(r.data); setTotal(r.meta?.total || 0); setLastPage(r.meta?.last_page || 1) })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [page, search])

  useEffect(() => { load() }, [load])

  async function del(id, nom) {
    if (!confirm(`Supprimer "${nom}" ?`)) return
    try { await contactsApi.delete(id); toast('Contact supprimé', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  return (
    <div className="content">
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-box">
          <i className="ti ti-search" />
          <input
            placeholder="Rechercher un contact…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
          />
        </div>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>{total} contact(s)</span>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn primary" onClick={() => setModal('create')}>
            <i className="ti ti-plus" /> Nouveau contact
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : items.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Contact</th><th>Catégorie</th><th>Ville</th>
                  <th>Téléphone</th><th>Email</th><th>Der. échange</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(c)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <Avatar name={c.nom} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{c.nom}</div>
                          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{c.information || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {c.famille_nom
                        ? <span style={{ background: c.famille_couleur + '22', color: c.famille_couleur, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{c.famille_nom}</span>
                        : '—'}
                    </td>
                    <td>{c.ville || '—'}</td>
                    <td>{c.tel || c.portable || '—'}</td>
                    <td>{c.email || '—'}</td>
                    <td>{fmtDate(c.dernier_echange)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setModal(c)}>
                          <i className="ti ti-edit" />
                        </button>
                        <button className="btn sm" onClick={() => del(c.id, c.nom)}>
                          <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon="users" message="Aucun contact — créez-en un !" />}
      </div>

      {lastPage > 1 && (
        <Pagination page={page} lastPage={lastPage} onChange={p => setPage(p)} />
      )}

      {modal && (
        <ContactModal
          contact={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { load(); setModal(null); toast('Contact enregistré', 'success') }}
        />
      )}
      {detail && (
        <ContactDetail
          contact={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setModal(detail); setDetail(null) }}
          onReload={load}
        />
      )}
    </div>
  )
}

// ── Modal création / édition ──────────────────────────────────
function ContactModal({ contact, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ type: 'entreprise', pays: 'France', remise_defaut: 0, ...(contact || {}) })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      if (contact?.id) await contactsApi.update(contact.id, form)
      else await contactsApi.create(form)
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{contact?.id ? 'Modifier le contact' : 'Nouveau contact'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid2">
              <FormGroup label="Nom / Raison sociale" required>
                <input className="form-control" value={form.nom || ''} onChange={f('nom')} required />
              </FormGroup>
              <FormGroup label="Type">
                <select className="form-control" value={form.type || 'entreprise'} onChange={f('type')}>
                  <option value="entreprise">Entreprise</option>
                  <option value="particulier">Particulier</option>
                </select>
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Téléphone">
                <input className="form-control" value={form.tel || ''} onChange={f('tel')} />
              </FormGroup>
              <FormGroup label="Portable">
                <input className="form-control" value={form.portable || ''} onChange={f('portable')} />
              </FormGroup>
            </div>
            <FormGroup label="Email">
              <input type="email" className="form-control" value={form.email || ''} onChange={f('email')} />
            </FormGroup>
            <div className="grid2">
              <FormGroup label="Adresse">
                <input className="form-control" value={form.adresse || ''} onChange={f('adresse')} />
              </FormGroup>
              <FormGroup label="Ville">
                <input className="form-control" value={form.ville || ''} onChange={f('ville')} />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Code postal">
                <input className="form-control" value={form.code_postal || ''} onChange={f('code_postal')} />
              </FormGroup>
              <FormGroup label="Pays">
                <input className="form-control" value={form.pays || 'France'} onChange={f('pays')} />
              </FormGroup>
            </div>
            <FormGroup label="SIRET">
              <input className="form-control" value={form.siret || ''} onChange={f('siret')} />
            </FormGroup>
            <FormGroup label="Informations / Secteur">
              <textarea className="form-control" rows={2} value={form.information || ''} onChange={f('information')} />
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Fiche détail contact — CRM complet ───────────────────────
function ContactDetail({ contact, onClose, onEdit, onReload }) {
  const { toast } = useApp()
  const [tab, setTab] = useState('fiche')       // 'fiche' | 'echanges' | 'documents'
  const [data, setData] = useState(null)           // détail complet depuis l'API
  const [loading, setLoading] = useState(true)

  // Charger le détail complet (avec interlocuteurs)
  useEffect(() => {
    contactsApi.get(contact.id)
      .then(r => setData(r.data))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [contact.id])

  const c = data || contact   // fallback sur les données de la liste pendant le chargement

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: 780 }}>

        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar name={c.nom} size={44} />
            <div>
              <div className="modal-title" style={{ fontSize: 16 }}>{c.nom}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {c.famille_nom
                  ? <span style={{ background: (c.famille_couleur || '#2e7d52') + '22', color: c.famille_couleur || 'var(--primary)', padding: '1px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, marginRight: 6 }}>{c.famille_nom}</span>
                  : null}
                {c.information || c.type}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={onEdit}><i className="ti ti-edit" /> Modifier</button>
            <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>

        {/* Onglets */}
        <div className="tabs" style={{ padding: '0 20px', marginBottom: 0 }}>
          {[
            { id: 'fiche', icon: 'user', label: 'Fiche' },
            { id: 'echanges', icon: 'messages', label: 'Échanges CRM' },
            { id: 'documents', icon: 'files', label: 'Documents' },
          ].map(t => (
            <div
              key={t.id}
              className={`tab${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <i className={`ti ti-${t.icon}`} style={{ marginRight: 5 }} />{t.label}
            </div>
          ))}
        </div>

        {/* Contenu */}
        <div className="modal-body" style={{ minHeight: 340 }}>
          {loading ? <Loading /> : (
            <>
              {tab === 'fiche' && <TabFiche contact={c} onReload={() => contactsApi.get(contact.id).then(r => setData(r.data))} toast={toast} />}
              {tab === 'echanges' && <TabEchanges contactId={contact.id} toast={toast} />}
              {tab === 'documents' && <TabDocuments contactId={contact.id} toast={toast} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Onglet Fiche ──────────────────────────────────────────────
function TabFiche({ contact, onReload, toast }) {
  const [interModal, setInterModal] = useState(false)

  return (
    <div>
      {/* Coordonnées */}
      <div className="grid2" style={{ marginBottom: 20 }}>
        <div>
          <p style={labelStyle}>Coordonnées</p>
          {contact.tel && <InfoLine icon="phone" text={contact.tel} />}
          {contact.portable && <InfoLine icon="device-mobile" text={contact.portable} />}
          {contact.email && <InfoLine icon="mail" text={contact.email} />}
          {contact.site_web && <InfoLine icon="world" text={contact.site_web} />}
          {!contact.tel && !contact.portable && !contact.email && <p style={{ fontSize: 13, color: 'var(--text3)' }}>—</p>}
        </div>
        <div>
          <p style={labelStyle}>Adresse</p>
          <p style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text2)' }}>
            {[contact.adresse, contact.code_postal, contact.ville, contact.pays].filter(Boolean).join(', ') || '—'}
          </p>
          {contact.siret && <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>SIRET : {contact.siret}</p>}
          {contact.tva_intracommunautaire && <p style={{ fontSize: 12, color: 'var(--text3)' }}>TVA : {contact.tva_intracommunautaire}</p>}
        </div>
      </div>

      <hr className="divider" />

      {/* Interlocuteurs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={labelStyle}>Interlocuteurs ({(contact.interlocuteurs || []).length})</p>
        <button className="btn sm" onClick={() => setInterModal(true)}>
          <i className="ti ti-plus" /> Ajouter
        </button>
      </div>

      {contact.interlocuteurs?.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contact.interlocuteurs.map(i => (
            <InterlocuteurCard key={i.id} interlocuteur={i} contactId={contact.id} onReload={onReload} toast={toast} />
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Aucun interlocuteur — ajoutez les personnes à contacter.</p>
      )}

      {interModal && (
        <InterlocuteurModal
          contactId={contact.id}
          onClose={() => setInterModal(false)}
          onSaved={() => { setInterModal(false); onReload(); toast('Interlocuteur ajouté', 'success') }}
        />
      )}
    </div>
  )
}

// ── Carte interlocuteur ───────────────────────────────────────
function InterlocuteurCard({ interlocuteur: i, contactId, onReload, toast }) {
  const [editModal, setEditModal] = useState(false)

  async function del() {
    if (!confirm(`Supprimer ${i.prenom || ''} ${i.nom} ?`)) return
    try {
      await fetch(`/api/interlocuteurs/${i.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('hs_token')}` }
      })
      onReload()
      toast('Interlocuteur supprimé', 'success')
    } catch (e) { toast(e.message, 'error') }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <Avatar name={`${i.prenom || ''} ${i.nom}`} size={36} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {i.civilite && <span style={{ color: 'var(--text3)', marginRight: 4 }}>{i.civilite}</span>}
          {i.prenom} {i.nom}
          {i.principal === 1 && <span style={{ marginLeft: 6, background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>Principal</span>}
        </div>
        {i.poste && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{i.poste}</div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
          {i.tel && <span style={{ fontSize: 12, color: 'var(--text2)' }}><i className="ti ti-phone" style={{ marginRight: 3 }} />{i.tel}</span>}
          {i.portable && <span style={{ fontSize: 12, color: 'var(--text2)' }}><i className="ti ti-device-mobile" style={{ marginRight: 3 }} />{i.portable}</span>}
          {i.email && <span style={{ fontSize: 12, color: 'var(--text2)' }}><i className="ti ti-mail" style={{ marginRight: 3 }} />{i.email}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="btn sm" onClick={() => setEditModal(true)}><i className="ti ti-edit" /></button>
        <button className="btn sm" onClick={del}><i className="ti ti-trash" style={{ color: 'var(--danger)' }} /></button>
      </div>
      {editModal && (
        <InterlocuteurModal
          contactId={contactId}
          interlocuteur={i}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); onReload(); toast('Interlocuteur mis à jour', 'success') }}
        />
      )}
    </div>
  )
}

// ── Modal interlocuteur ───────────────────────────────────────
function InterlocuteurModal({ contactId, interlocuteur, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ civilite: '', prenom: '', nom: '', poste: '', tel: '', portable: '', email: '', principal: 0, ...(interlocuteur || {}) })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      const url = interlocuteur?.id ? `/api/interlocuteurs/${interlocuteur.id}` : '/api/interlocuteurs'
      const method = interlocuteur?.id ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hs_token')}` },
        body: JSON.stringify({ ...form, contact_id: contactId })
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.message) }
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" style={{ zIndex: 200 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <span className="modal-title">{interlocuteur?.id ? 'Modifier l\'interlocuteur' : 'Nouvel interlocuteur'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid2">
              <FormGroup label="Civilité">
                <select className="form-control" value={form.civilite || ''} onChange={f('civilite')}>
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                  <option value="Dr">Dr</option>
                </select>
              </FormGroup>
              <FormGroup label="Poste / Fonction">
                <input className="form-control" value={form.poste || ''} onChange={f('poste')} placeholder="Directeur, Comptable…" />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Prénom">
                <input className="form-control" value={form.prenom || ''} onChange={f('prenom')} />
              </FormGroup>
              <FormGroup label="Nom" required>
                <input className="form-control" value={form.nom || ''} onChange={f('nom')} required />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Téléphone">
                <input className="form-control" value={form.tel || ''} onChange={f('tel')} />
              </FormGroup>
              <FormGroup label="Portable">
                <input className="form-control" value={form.portable || ''} onChange={f('portable')} />
              </FormGroup>
            </div>
            <FormGroup label="Email">
              <input type="email" className="form-control" value={form.email || ''} onChange={f('email')} />
            </FormGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
              <input
                type="checkbox"
                checked={!!form.principal}
                onChange={e => setForm(p => ({ ...p, principal: e.target.checked ? 1 : 0 }))}
                style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
              />
              Contact principal
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Onglet Échanges CRM ───────────────────────────────────────
function TabEchanges({ contactId, toast }) {
  const [echanges, setEchanges] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)

  const load = () => {
    setLoading(true)
    contactsApi.echanges(contactId)
      .then(r => setEchanges(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [contactId])

  const TYPE_ICONS = { Téléphone: 'phone', Email: 'mail', 'Rendez-vous': 'calendar', Courrier: 'mail-opened', Rencontre: 'users', Autre: 'dots' }
  const TYPE_COLORS = { Téléphone: '#1565c0', Email: '#2e7d52', 'Rendez-vous': '#e65100', Courrier: '#6a1b9a', Rencontre: '#2e7d52', Autre: '#868e96' }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn primary" onClick={() => setModal(true)}>
          <i className="ti ti-plus" /> Nouvel échange
        </button>
      </div>

      {loading ? <Loading /> : echanges.length === 0 ? (
        <Empty icon="messages" message="Aucun échange enregistré" />
      ) : (
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          {/* Ligne verticale timeline */}
          <div style={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, background: 'var(--border)' }} />

          {echanges.map((e, idx) => {
            const icon = TYPE_ICONS[e.type_nom] || 'dots'
            const color = TYPE_COLORS[e.type_nom] || '#868e96'
            return (
              <div key={e.id} style={{ position: 'relative', marginBottom: 18 }}>
                {/* Pastille */}
                <div style={{ position: 'absolute', left: -28, top: 2, width: 20, height: 20, borderRadius: '50%', background: color + '22', border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`ti ti-${icon}`} style={{ fontSize: 10, color }} />
                </div>

                {/* Carte */}
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{e.objet}</div>
                      {e.contenu && <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{e.contenu}</p>}
                    </div>
                    <EchangeActions echange={e} onDeleted={load} toast={toast} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      <i className="ti ti-clock" style={{ marginRight: 3 }} />
                      {fmtDate(e.date_echange)}
                    </span>
                    {e.type_nom && (
                      <span style={{ fontSize: 11, color, fontWeight: 600 }}>
                        <i className={`ti ti-${icon}`} style={{ marginRight: 3 }} />{e.type_nom}
                      </span>
                    )}
                    {e.rappel_le && !e.rappel_fait && (
                      <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
                        <i className="ti ti-bell" style={{ marginRight: 3 }} />Rappel : {fmtDate(e.rappel_le)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {modal && (
        <EchangeModal
          contactId={contactId}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); toast('Échange enregistré', 'success') }}
        />
      )}
    </div>
  )
}

// ── Actions échange (supprimer) ───────────────────────────────
function EchangeActions({ echange, onDeleted, toast }) {
  async function del() {
    if (!confirm('Supprimer cet échange ?')) return
    try {
      await fetch(`/api/echanges/${echange.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('hs_token')}` }
      })
      onDeleted()
      toast('Échange supprimé', 'success')
    } catch (e) { toast(e.message, 'error') }
  }
  return (
    <button className="btn sm" onClick={del} title="Supprimer">
      <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
    </button>
  )
}

// ── Modal nouvel échange ──────────────────────────────────────
function EchangeModal({ contactId, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({
    objet: '',
    contenu: '',
    type_echange_id: '',
    date_echange: new Date().toISOString().slice(0, 16),
    rappel_le: '',
  })
  const [types, setTypes] = useState([])
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  useEffect(() => {
    fetch('/api/parametres', { headers: { Authorization: `Bearer ${localStorage.getItem('hs_token')}` } })
      .then(r => r.json())
      .then(d => setTypes(d.data?.types_echange || []))
      .catch(() => { })
  }, [])

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      await echangesApi.create({ ...form, contact_id: contactId })
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" style={{ zIndex: 200 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <span className="modal-title">Nouvel échange</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid2">
              <FormGroup label="Type">
                <select className="form-control" value={form.type_echange_id} onChange={f('type_echange_id')}>
                  <option value="">— Sélectionner —</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                </select>
              </FormGroup>
              <FormGroup label="Date">
                <input type="datetime-local" className="form-control" value={form.date_echange} onChange={f('date_echange')} />
              </FormGroup>
            </div>
            <FormGroup label="Objet" required>
              <input className="form-control" value={form.objet} onChange={f('objet')} placeholder="Ex: Appel de suivi, Envoi devis…" required />
            </FormGroup>
            <FormGroup label="Notes / Compte-rendu">
              <textarea className="form-control" rows={4} value={form.contenu} onChange={f('contenu')} placeholder="Détails de l'échange…" />
            </FormGroup>
            <FormGroup label="Rappel le">
              <input type="date" className="form-control" value={form.rappel_le} onChange={f('rappel_le')} />
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Onglet Documents ──────────────────────────────────────────
function TabDocuments({ contactId, toast }) {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    contactsApi.documents(contactId)
      .then(r => setDocs(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [contactId])

  const STATUT_COLORS = { regle: 'var(--success)', en_attente: 'var(--warning)', partiel: 'var(--warning)', refuse: 'var(--danger)', annule: 'var(--danger)', brouillon: 'var(--text3)', envoye: 'var(--info)', accepte: 'var(--success)', transforme: '#6a1b9a' }
  const STATUTS_FR = { brouillon: 'Brouillon', envoye: 'Envoyé', accepte: 'Accepté', refuse: 'Refusé', en_attente: 'En attente', partiel: 'Partiel', regle: 'Réglé', annule: 'Annulé', transforme: 'Transformé' }
  const TYPES_FR = { devis: 'Devis', facture: 'Facture', bon_commande: 'BC', bon_livraison: 'BL', avoir: 'Avoir' }

  const totalTTC = docs.reduce((s, d) => s + parseFloat(d.total_ttc || 0), 0)
  const totalRegle = docs.reduce((s, d) => s + parseFloat(d.montant_regle || 0), 0)

  return (
    <div>
      {/* Résumé rapide */}
      {docs.length > 0 && (
        <div className="grid3" style={{ marginBottom: 16 }}>
          <div style={statMiniStyle}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Total documents</span>
            <strong style={{ fontSize: 18 }}>{docs.length}</strong>
          </div>
          <div style={statMiniStyle}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Total TTC facturé</span>
            <strong style={{ fontSize: 18, color: 'var(--primary)' }}>{fmt(totalTTC)}</strong>
          </div>
          <div style={statMiniStyle}>
            <span style={{ fontSize: 11, color: docs.length && totalRegle < totalTTC ? 'var(--danger)' : 'var(--success)' }}>Solde restant</span>
            <strong style={{ fontSize: 18, color: totalRegle < totalTTC ? 'var(--danger)' : 'var(--success)' }}>{fmt(totalTTC - totalRegle)}</strong>
          </div>
        </div>
      )}

      {loading ? <Loading /> : docs.length === 0 ? (
        <Empty icon="files" message="Aucun document pour ce contact" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Numéro</th><th>Type</th><th>Date</th><th>Montant TTC</th><th>Solde</th><th>Statut</th></tr>
            </thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.id}>
                  <td><strong>{d.numero}</strong></td>
                  <td><span className={`doc-type t-${d.type}`}>{TYPES_FR[d.type] || d.type}</span></td>
                  <td>{fmtDate(d.date_document)}</td>
                  <td><strong>{fmt(d.total_ttc)}</strong></td>
                  <td>
                    {parseFloat(d.solde) > 0
                      ? <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{fmt(d.solde)}</span>
                      : <span style={{ color: 'var(--success)', fontSize: 12 }}>✓ Réglé</span>}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 600, color: STATUT_COLORS[d.statut] || 'var(--text3)' }}>
                      {STATUTS_FR[d.statut] || d.statut}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Petits helpers UI ─────────────────────────────────────────
const labelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--text3)',
  marginBottom: 8, letterSpacing: '.05em', textTransform: 'uppercase'
}
const statMiniStyle = {
  background: 'var(--bg3)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: '10px 14px',
  display: 'flex', flexDirection: 'column', gap: 4
}

function InfoLine({ icon, text }) {
  return (
    <p style={{ fontSize: 13, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
      <i className={`ti ti-${icon}`} style={{ color: 'var(--text3)', fontSize: 14 }} />
      {text}
    </p>
  )
}
